import { useEffect, useMemo, useState } from 'react';
import {
  fetchWorkload,
  type MetricCell,
  type WorkloadResponse,
} from '../api/work';
import { WorkloadPanels } from '../components/work/workload/WorkloadPanels';
import './WorkloadPage.css';

/**
 * Per-member load, across dimensions — the facts, without a ranking.
 *
 * **Deliberately no "most loaded" column and no default sort by volume.**
 * The predecessor's endpoint earned that constraint and its docstring says
 * why: different roles produce different signals, so one dimension alone
 * misleads. A support engineer with forty open tickets and a core
 * developer with four are not comparable on open tickets, and a table
 * that sorts by it invites exactly that comparison. Sorting is available
 * per column because the reader may have a question; it is not applied
 * for them.
 *
 * **Every cell distinguishes three kinds of nothing.** A value the metric
 * computed as zero, a metric that declined with a reason, and a metric
 * nobody computed all look like an empty cell if you let them. They read
 * `0`, `–` with the reason on hover, and `·` respectively.
 */

/** The four bands, in the order the route reports their members. */
const BANDS: Array<{ title: string; hint: string; metrics: string[] }> = [
  {
    title: 'Load now',
    hint: 'open work, at this moment',
    metrics: ['in_progress_count', 'stale_task_count', 'overdue_task_count'],
  },
  {
    title: 'Did, in window',
    hint: 'counts over the metric window',
    metrics: [
      'jira_actions_7d_person',
      'jira_done_count_7d_person',
      'github_commit_count_7d_person',
      'github_pr_opened_count_7d_person',
      'github_pr_merged_count_7d_person',
      'github_review_count_7d_person',
    ],
  },
  {
    title: 'How they work',
    hint: 'cadence and responsiveness',
    metrics: [
      'active_weeks_count',
      'coordination_footprint',
      'ticket_reaction_time_median_hours',
      'review_turnaround_by_reviewer',
      'pr_author_load',
      'commit_pr_ratio_monthly',
    ],
  },
  {
    title: 'Kind of work',
    hint: 'shares of what they touched',
    metrics: [
      'focus_project_share',
      'reactive_project_share',
      'growth_project_share',
      'fixed_time_task_share',
      'unplanned_tasks_count',
      'hardware_throughput',
    ],
  },
];

/** Short column heads. The full metric name is the cell's title. */
const SHORT: Record<string, string> = {
  in_progress_count: 'open',
  stale_task_count: 'stale',
  overdue_task_count: 'overdue',
  jira_actions_7d_person: 'jira',
  jira_done_count_7d_person: 'done',
  github_commit_count_7d_person: 'commits',
  github_pr_opened_count_7d_person: 'PRs',
  github_pr_merged_count_7d_person: 'merged',
  github_review_count_7d_person: 'reviews',
  active_weeks_count: 'weeks',
  coordination_footprint: 'footprint',
  ticket_reaction_time_median_hours: 'react',
  review_turnaround_by_reviewer: 'turnaround',
  pr_author_load: 'PR load',
  commit_pr_ratio_monthly: 'c/PR',
  focus_project_share: 'focus',
  reactive_project_share: 'reactive',
  growth_project_share: 'growth',
  fixed_time_task_share: 'fixed',
  unplanned_tasks_count: 'unplanned',
  hardware_throughput: 'hardware',
};

/** Metrics whose value is a 0–1 share and reads better as a percentage. */
const AS_SHARE = new Set([
  'focus_project_share',
  'reactive_project_share',
  'growth_project_share',
  'fixed_time_task_share',
]);

/** Metrics measured in hours. */
const AS_HOURS = new Set([
  'ticket_reaction_time_median_hours',
  'review_turnaround_by_reviewer',
]);

function relativeAge(ms: number): string {
  const min = Math.floor(ms / 60000);
  if (min < 2) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

function render(metric: string, cell: MetricCell): string {
  if (cell.value === null) {
    // `reason === null` with a null value would be a metric that ran and
    // said nothing, which the catalogue does not do — but if one ever
    // does, it reads as declined rather than as uncomputed.
    return cell.reason === 'not computed' ? '·' : '–';
  }
  const v = cell.value;
  if (AS_SHARE.has(metric)) return `${Math.round(v * 100)}%`;
  if (AS_HOURS.has(metric)) return v >= 48 ? `${Math.round(v / 24)}d` : `${Math.round(v)}h`;
  if (Number.isInteger(v)) return String(v);
  return v.toFixed(1);
}

function cellTitle(metric: string, cell: MetricCell): string {
  if (cell.value === null) {
    return cell.reason
      ? `${metric} — ${cell.reason}`
      : `${metric} — no value and no reason given`;
  }
  const parts = [`${metric} = ${cell.value}`];
  if (cell.breakdown && typeof cell.breakdown === 'object') {
    parts.push(JSON.stringify(cell.breakdown));
  }
  return parts.join('  ·  ');
}

export function WorkloadPage() {
  const [resp, setResp] = useState<WorkloadResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    fetchWorkload()
      .then((r) => {
        if (live) {
          setResp(r);
          setError(null);
        }
      })
      .catch((e: unknown) => {
        if (live) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, []);

  const columns = useMemo(
    () => BANDS.flatMap((b) => b.metrics),
    [],
  );

  const members = useMemo(() => {
    const rows = resp?.members ?? [];
    if (!sortBy) return rows;
    // Nulls sort last whichever way the column goes — an absent
    // measurement is not a low score.
    return [...rows].sort((a, b) => {
      const x = a.metrics[sortBy]?.value;
      const y = b.metrics[sortBy]?.value;
      if (x === null || x === undefined) return 1;
      if (y === null || y === undefined) return -1;
      return y - x;
    });
  }, [resp, sortBy]);

  /**
   * Members grouped by their home lane, or one flat group.
   *
   * The predecessor grouped this grid by lane and the port did not, because
   * the route returned no lane. It does now. Grouping is what makes the
   * no-ranking rule workable: a support engineer and a core developer are
   * genuinely not comparable on open tickets, and putting them in separate
   * blocks says so structurally rather than in a footnote.
   *
   * Sorting stays within a lane. A sort across lanes would reintroduce
   * exactly the comparison the grouping exists to prevent.
   */
  const groups = useMemo(() => {
    const rows = members;
    if (!resp?.lanes_resolved) return [{ lane: null, rows }];
    const byLane = new Map<string | null, typeof rows>();
    for (const m of rows) {
      const key = m.native_lane ?? null;
      byLane.set(key, [...(byLane.get(key) ?? []), m]);
    }
    return [...byLane.entries()]
      .sort(([a], [b]) => (a ?? '\uffff').localeCompare(b ?? '\uffff'))
      .map(([lane, rows]) => ({ lane, rows }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members, resp]);

  if (error) {
    return (
      <div className="workload-page">
        <h1 className="workload-page__title">Workload</h1>
        <p className="workload-page__note">{error}</p>
        <p className="workload-dim">
          The figures are metrics <code>work.metrics</code> computes on a
          timer. Before its first pass there is nothing stored to show.
        </p>
      </div>
    );
  }

  if (!resp) {
    return (
      <div className="workload-page">
        <h1 className="workload-page__title">Workload</h1>
        <p className="workload-dim">{loading ? 'Loading…' : 'No data.'}</p>
      </div>
    );
  }

  return (
    <div className="workload-page">
      <header className="workload-page__header">
        <div>
          <h1 className="workload-page__title">Workload</h1>
          <p className="workload-page__note">
            {resp.members.length} people, {columns.length} dimensions. Facts
            only — no ranking, and no column is a score.
            {resp.lanes_resolved === 0 &&
              ' Ungrouped: work.trends places people in lanes and is not answering.'}
          </p>
        </div>
        <span
          className="workload-age"
          title={new Date(resp.computed_at_ms).toISOString()}
        >
          computed {relativeAge(resp.age_ms)}
        </span>
      </header>

      {/* The diagrams, which is what this page was. Reported as "the
          workload page consisted a few carefully designed diagrams but now
          it only shows a matrix" — it did, because the route it read
          returns scalars and stacked bars need segments and evidence.

          They come first because they are the page; the matrix below
          carries fourteen dimensions they do not — cycle time, reaction
          time, the project shares — so deleting it to restore them would
          trade one loss for another. */}
      <WorkloadPanels workload={resp} />

      <div className="workload-matrix-head">
        <span className="workload-matrix-head__title">All dimensions</span>
        <span className="workload-dim">
          Every person-scope metric <code>work.metrics</code> computed in its
          last pass, including the fourteen the panels above do not cover.
        </span>
      </div>

      <div className="workload-scroll">
        <table className="workload-table">
          <thead>
            <tr className="workload-table__bands">
              <th />
              {BANDS.map((b) => (
                <th
                  key={b.title}
                  colSpan={b.metrics.length}
                  title={b.hint}
                  className="workload-band"
                >
                  {b.title}
                </th>
              ))}
            </tr>
            <tr>
              <th className="workload-table__who">person</th>
              {columns.map((m) => (
                <th key={m} className="workload-table__col">
                  <button
                    type="button"
                    className={`workload-sort${sortBy === m ? ' is-active' : ''}`}
                    onClick={() => setSortBy(sortBy === m ? null : m)}
                    title={`Sort by ${m}`}
                  >
                    {SHORT[m] ?? m}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          {groups.map((group) => (
            <tbody key={group.lane ?? 'ungrouped'}>
              {group.lane !== null && (
                <tr className="workload-group">
                  <th scope="rowgroup" colSpan={columns.length + 1}>
                    {group.lane.toUpperCase()}
                    <span className="workload-dim">
                      {' '}
                      · {group.rows.length} of {members.length}
                    </span>
                  </th>
                </tr>
              )}
              {group.rows.map((member) => (
                <tr key={member.person}>
                  <th scope="row" className="workload-table__who">
                    {member.person}
                  </th>
                  {columns.map((m) => {
                    const cell = member.metrics[m];
                    const empty = !cell || cell.value === null;
                    return (
                      <td
                        key={m}
                        className={`workload-cell${empty ? ' is-empty' : ''}${
                          sortBy === m ? ' is-sorted' : ''
                        }`}
                        title={cell ? cellTitle(m, cell) : m}
                      >
                        {cell ? render(m, cell) : '·'}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          ))}
        </table>
      </div>

      <p className="workload-legend">
        <span>
          <strong>–</strong> the metric declined; hover for its reason
        </span>
        <span>
          <strong>·</strong> not computed in this pass
        </span>
        <span className="workload-dim">
          every figure comes from one metric run, so the row is internally
          consistent
        </span>
      </p>
    </div>
  );
}
